import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
} from 'react-native';
import { showAlert } from '../services/alert';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { openRazorpayCheckout } from '../services/payment';
import Toast from 'react-native-toast-message';
import Icon from 'react-native-vector-icons/Ionicons';
import { getMembershipStatus, getPlans, createPaymentOrder, verifyPayment, reportPaymentFailure, activateFreePlan, getProfileByUserId } from '../services/api';
import { THEME } from '../components';
import { useLanguage } from '../context/LanguageContext';
import { useAuthContext } from '../context/AuthContext';
import type { MembershipStatus, MembershipPlan, ProfileStatus } from '../types';

const PLAN_COLORS: Record<string, string> = {
  BASIC: '#5C6BC0',
  STANDARD: '#C2185B',
  PREMIUM: '#F39C12',
  FREE_TRIAL: '#27AE60',
};

const PLAN_RANK: Record<string, number> = {
  FREE_TRIAL: 0,
  BASIC: 1,
  STANDARD: 2,
  PREMIUM: 3,
};

const FeatureRow: React.FC<{ label: string; available: boolean }> = ({ label, available }) => (
  <View style={styles.planFeature}>
    <Icon
      name={available ? 'checkmark-circle' : 'close-circle-outline'}
      size={16}
      color={available ? '#27AE60' : THEME.border}
    />
    <Text style={[styles.planFeatureText, !available && styles.planFeatureUnavailable]}>
      {label}
    </Text>
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────

const MembershipScreen: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuthContext();
  const [status, setStatus] = useState<MembershipStatus | null>(null);
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [profileStatus, setProfileStatus] = useState<ProfileStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      loadData();
    }, []),
  );

  const loadData = async () => {
    try {
      const [statusRes, plansRes] = await Promise.all([getMembershipStatus(), getPlans()]);
      setStatus(statusRes.data);
      setPlans(plansRes.data.map((p: MembershipPlan) => ({
        ...p,
        amountLabel: p.amountPaise === 0 ? 'FREE' : '₹' + (p.amountPaise / 100).toLocaleString('en-IN'),
      })));
    } catch {
      Toast.show({ type: 'error', text1: 'Failed to load membership info' });
    } finally {
      setLoading(false);
    }
    if (user?.sub) {
      try {
        const profileRes = await getProfileByUserId(encodeURIComponent(user.sub));
        setProfileStatus(profileRes.data?.profileStatus ?? null);
      } catch {
        setProfileStatus(null);
      }
    }
  };

  const handleBuy = (plan: MembershipPlan) => {
    showAlert(
      `${t('mem.buyPlanTitle', 'Buy')} ${plan.label} ${t('mem.plan', 'Plan')}`,
      `${plan.durationLabel} · ${plan.contactLimit} ${t('mem.contactViews', 'contact views')} · ${plan.amountLabel}`,
      [
        { text: t('btn.cancel', 'Cancel'), style: 'cancel' },
        { text: t('mem.proceed', 'Proceed'), onPress: () => startPayment(plan) },
      ],
    );
  };

  const startPayment = async (plan: MembershipPlan) => {
    setPurchasing(plan.planType);
    let razorpayOrderId: string | undefined;
    try {
      // Step 1: Create order on backend
      const orderRes = await createPaymentOrder(plan.planType);
      const { amountPaise, currency, keyId } = orderRes.data;
      razorpayOrderId = orderRes.data.razorpayOrderId;

      // Step 2: Open Razorpay checkout
      const options = {
        description: `JeevanSanyog ${plan.label} Membership`,
        currency,
        key: keyId,
        amount: String(amountPaise),
        order_id: razorpayOrderId,
        name: 'JeevanSanyog',
        theme: { color: THEME.primary },
      };

      const paymentData = await openRazorpayCheckout(options);

      // Step 3: Verify with backend — activates membership
      await verifyPayment({
        razorpayOrderId,
        razorpayPaymentId: paymentData.razorpay_payment_id,
        razorpaySignature: paymentData.razorpay_signature,
        planType: plan.planType,
      });

      Toast.show({ type: 'success', text1: `${plan.label} plan activated!`, text2: 'Enjoy your membership.' });
      loadData();
    } catch (e: unknown) {
      const err = e as { code?: number; description?: string; message?: string; razorpay_payment_id?: string };
      // Razorpay checkout returns { code, description } on cancel/failure
      if (err?.code === 0 || err?.description?.includes('cancelled')) {
        Toast.show({ type: 'info', text1: 'Payment cancelled' });
      } else {
        Toast.show({ type: 'error', text1: 'Payment failed', text2: err?.message ?? err?.description ?? 'Please try again' });
        // Report failure to backend so the order is marked FAILED
        if (razorpayOrderId) {
          reportPaymentFailure({
            razorpayOrderId,
            razorpayPaymentId: err?.razorpay_payment_id,
            reason: err?.description ?? err?.message ?? 'Payment failed',
          }).catch(() => {}); // fire-and-forget, don't block UI
        }
      }
    } finally {
      setPurchasing(null);
    }
  };

  const handleClaimFree = (plan: MembershipPlan) => {
    showAlert(
      `Claim Free ${plan.label} Plan`,
      `Activate ${plan.durationLabel} · ${plan.contactLimit} contact views at no cost.`,
      [
        { text: t('btn.cancel', 'Cancel'), style: 'cancel' },
        { text: 'Claim Now', onPress: () => claimFreePlan(plan) },
      ],
    );
  };

  const claimFreePlan = async (plan: MembershipPlan) => {
    setPurchasing(plan.planType);
    try {
      await activateFreePlan(plan.planType);
      Toast.show({ type: 'success', text1: `${plan.label} plan activated!`, text2: 'Enjoy your membership.' });
      loadData();
    } catch (e: unknown) {
      const err = e as { message?: string };
      Toast.show({ type: 'error', text1: 'Activation failed', text2: err?.message ?? 'Please try again' });
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={THEME.primary} />
      </View>
    );
  }

  const planColor = status?.planType ? (PLAN_COLORS[status.planType] ?? THEME.primary) : THEME.textLight;
  const profileNotActive = profileStatus !== null && profileStatus !== 'ACTIVE' && profileStatus !== 'PENDING_APPROVAL';

  return (
    <SafeAreaView style={styles.safeArea} edges={['top']}>
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>

      {/* Profile not approved — block purchases */}
      {profileNotActive && (
        <View style={styles.profileBlockBanner}>
          <Icon name="time-outline" size={20} color="#92400E" />
          <Text style={styles.profileBlockText}>
            {profileStatus === 'DRAFT'
              ? 'Please complete and submit your profile before purchasing a plan.'
              : 'Your profile must be submitted before purchasing a membership plan.'}
          </Text>
        </View>
      )}

      {/* Current Status Card */}
      <View style={[styles.statusCard, { borderLeftColor: planColor }]}>
        <Text style={styles.statusTitle}>{t('mem.currentMembership', 'Current Membership')}</Text>

        {status?.active ? (
          <>
            <View style={styles.statusRow}>
              <View style={[styles.planBadge, { backgroundColor: planColor }]}>
                <Text style={styles.planBadgeText}>{status.planType?.replace('_', ' ')}</Text>
              </View>
              <Text style={styles.expiryText}>{t('mem.expires', 'Expires')} {status.expiryDate}</Text>
            </View>

            <View style={styles.viewsRow}>
              <View style={styles.viewsStat}>
                <Text style={styles.viewsNum}>{status.remainingViews}</Text>
                <Text style={styles.viewsLabel}>{t('mem.remaining', 'Remaining')}</Text>
              </View>
              <View style={styles.viewsDivider} />
              <View style={styles.viewsStat}>
                <Text style={styles.viewsNum}>{status.contactsViewed}</Text>
                <Text style={styles.viewsLabel}>{t('mem.used', 'Used')}</Text>
              </View>
              <View style={styles.viewsDivider} />
              <View style={styles.viewsStat}>
                <Text style={styles.viewsNum}>{status.contactViewLimit}</Text>
                <Text style={styles.viewsLabel}>{t('mem.total', 'Total')}</Text>
              </View>
            </View>

            {/* Progress bar */}
            <View style={styles.progressBar}>
              <View
                style={[styles.progressFill, {
                  width: `${Math.min(100, (status.contactsViewed / status.contactViewLimit) * 100)}%`,
                  backgroundColor: planColor,
                }]}
              />
            </View>
          </>
        ) : (
          <View style={styles.noMembership}>
            <Icon name="lock-closed-outline" size={32} color={THEME.textLight} />
            <Text style={styles.noMembershipText}>
              {status?.planType ? t('mem.expired', 'Your membership has expired') : t('mem.noMembership', 'No active membership')}
            </Text>
            <Text style={styles.noMembershipSub}>{t('mem.purchaseHint', 'Purchase a plan below to view contact details')}</Text>
          </View>
        )}
      </View>

      {/* Plans */}
      <Text style={styles.plansTitle}>{t('mem.choosePlan', 'Choose a Plan')}</Text>

      {plans.map(plan => {
        const color = PLAN_COLORS[plan.planType];
        const isCurrent = status?.active && status.planType === plan.planType;
        const isBuying = purchasing === plan.planType;
        const isDowngrade = status?.active
          && !!status.planType
          && (PLAN_RANK[plan.planType] ?? 0) < (PLAN_RANK[status.planType] ?? 0);

        return (
          <View key={plan.planType} style={[styles.planCard, isCurrent && styles.planCardActive]}>
            {plan.promotionLabel && (
              <View style={styles.promoBanner}>
                <Icon name="pricetag-outline" size={12} color="#fff" />
                <Text style={styles.promoBannerText}>{plan.promotionLabel}</Text>
              </View>
            )}

            {isCurrent && (
              <View style={[styles.currentBadge, { backgroundColor: color }]}>
                <Text style={styles.currentBadgeText}>{t('mem.currentPlan', 'Current Plan')}</Text>
              </View>
            )}

            <View style={styles.planHeader}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.planName, { color }]}>{plan.label}</Text>
                <View style={styles.durationRow}>
                  {plan.originalDurationLabel && (
                    <Text style={styles.originalValue}>{plan.originalDurationLabel}</Text>
                  )}
                  <Text style={styles.planDuration}>{plan.durationLabel}</Text>
                </View>
              </View>
              <Text style={[styles.planPrice, { color }]}>{plan.amountLabel}</Text>
            </View>

            {/* Contact views row — shows original count struck through when promo changes it */}
            <View style={styles.planFeature}>
              <Icon name="checkmark-circle" size={16} color="#27AE60" />
              <Text style={styles.planFeatureText}>
                {plan.originalContactLimit > 0 && (
                  <Text style={styles.originalValue}>{plan.originalContactLimit} </Text>
                )}
                <Text style={{ fontWeight: '700', color: THEME.text }}>{plan.contactLimit}</Text>
                {' '}{t('mem.contactDetails', 'contact views')}
              </Text>
            </View>
            <FeatureRow label={t('mem.shortlistProfiles', 'Shortlist profiles')} available={true} />
            <FeatureRow label={t('mem.sendInterest', 'Send Interest to profiles')} available={plan.canSendInterest} />
            <FeatureRow label={t('mem.featuredFeed', 'Featured in search results')} available={plan.featured} />
            <FeatureRow label={t('mem.verifiedOnly', 'Verified profiles only')} available={true} />

            {isDowngrade ? (
              <View style={styles.downgradeBanner}>
                <Icon name="lock-closed-outline" size={14} color={THEME.textLight} />
                <Text style={styles.downgradeText}>{t('mem.downgradeBlocked', 'Not available while your current plan is active')}</Text>
              </View>
            ) : plan.amountPaise === 0 ? (
              profileNotActive ? (
                <View style={styles.freeProfileNote}>
                  <Icon name="information-circle-outline" size={16} color="#065F46" />
                  <Text style={styles.freeProfileNoteText}>Submit your profile first to claim this plan</Text>
                </View>
              ) : (
                <TouchableOpacity
                  style={[styles.buyBtn, styles.freePlanBtn, (isBuying || purchasing !== null) && styles.buyBtnDisabled]}
                  onPress={() => handleClaimFree(plan)}
                  disabled={isBuying || purchasing !== null}
                  activeOpacity={0.8}>
                  {isBuying ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <Text style={styles.buyBtnText}>{isCurrent ? t('mem.renewPlan', 'Renew Plan') : t('mem.claimFree', 'Claim Free')}</Text>
                  )}
                </TouchableOpacity>
              )
            ) : (
              <TouchableOpacity
                style={[styles.buyBtn, { backgroundColor: color }, (isBuying || purchasing !== null || profileNotActive) && styles.buyBtnDisabled]}
                onPress={() => handleBuy(plan)}
                disabled={isBuying || purchasing !== null || profileNotActive}
                activeOpacity={0.8}>
                {isBuying ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.buyBtnText}>{isCurrent ? t('mem.renewPlan', 'Renew Plan') : t('mem.buyPlan', 'Buy Plan')}</Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        );
      })}

      {/* Payment info box */}
      <View style={styles.paymentInfoBox}>
        <View style={styles.paymentInfoRow}>
          <Icon name="shield-checkmark-outline" size={16} color="#1565C0" />
          <Text style={styles.paymentInfoText}>
            {t('mem.disclaimer', 'Payments are processed securely via Razorpay.')}
          </Text>
        </View>
        <View style={styles.paymentInfoDivider} />
        <View style={styles.paymentInfoRow}>
          <Icon name="information-circle-outline" size={16} color="#1565C0" />
          <Text style={styles.paymentInfoText}>
            {t('mem.activationNote',
              'Membership activates instantly after payment. If it does not appear, close and reopen the app.')}
          </Text>
        </View>
        <View style={styles.paymentInfoRow}>
          <Icon name="headset-outline" size={16} color="#1565C0" />
          <Text style={styles.paymentInfoText}>
            {t('mem.supportNote',
              'Still not activated? Contact us via WhatsApp or Email — find us in Help & Support. We resolve payment issues within a few hours.')}
          </Text>
        </View>
        <View style={styles.paymentInfoDivider} />
        <TouchableOpacity style={styles.paymentInfoRow} onPress={() => Linking.openURL('https://jeevanSanyog.com/refund-policy')} activeOpacity={0.7}>
          <Icon name="document-text-outline" size={16} color="#1565C0" />
          <Text style={[styles.paymentInfoText, styles.refundPolicyLink]}>
            {t('mem.refundPolicy', 'Refund Policy')}
          </Text>
        </TouchableOpacity>
      </View>

    </ScrollView>
    </SafeAreaView>
  );
};

export default MembershipScreen;

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: THEME.bg },
  container: { flex: 1, backgroundColor: THEME.bg },
  content: { padding: 20, paddingBottom: 48 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: THEME.bg },

  statusCard: {
    backgroundColor: THEME.card,
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
    borderLeftWidth: 5,
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  statusTitle: { fontSize: 13, fontWeight: '700', color: THEME.textLight, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.8 },
  statusRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  planBadge: { paddingHorizontal: 12, paddingVertical: 4, borderRadius: 20 },
  planBadgeText: { color: '#fff', fontSize: 13, fontWeight: '700' },
  expiryText: { fontSize: 13, color: THEME.textLight },
  viewsRow: { flexDirection: 'row', justifyContent: 'space-around', marginBottom: 12 },
  viewsStat: { alignItems: 'center' },
  viewsNum: { fontSize: 24, fontWeight: '900', color: THEME.text },
  viewsLabel: { fontSize: 12, color: THEME.textLight, marginTop: 2 },
  viewsDivider: { width: 1, backgroundColor: THEME.border },
  progressBar: { height: 6, backgroundColor: THEME.border, borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 3 },

  noMembership: { alignItems: 'center', paddingVertical: 12, gap: 8 },
  noMembershipText: { fontSize: 15, fontWeight: '700', color: THEME.text },
  noMembershipSub: { fontSize: 13, color: THEME.textLight, textAlign: 'center' },

  plansTitle: { fontSize: 18, fontWeight: '800', color: THEME.text, marginBottom: 16 },

  planCard: {
    backgroundColor: THEME.card,
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1.5,
    borderColor: THEME.border,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowRadius: 6,
    elevation: 2,
  },
  planCardActive: { borderColor: THEME.primary, shadowOpacity: 0.1 },
  promoBanner: { flexDirection: 'row', alignItems: 'center', gap: 4, alignSelf: 'flex-start', backgroundColor: '#E53935', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 20, marginBottom: 8 },
  promoBannerText: { color: '#fff', fontSize: 11, fontWeight: '800' },
  currentBadge: { alignSelf: 'flex-start', paddingHorizontal: 10, paddingVertical: 3, borderRadius: 20, marginBottom: 10 },
  currentBadgeText: { color: '#fff', fontSize: 11, fontWeight: '700' },
  planHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  planName: { fontSize: 20, fontWeight: '900' },
  planDuration: { fontSize: 13, color: THEME.textLight, marginTop: 2 },
  planPrice: { fontSize: 26, fontWeight: '900' },
  planFeature: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  planFeatureText: { fontSize: 13, color: THEME.textLight, flex: 1 },
  planFeatureUnavailable: { textDecorationLine: 'line-through', color: THEME.border },
  durationRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
  originalValue: { fontSize: 13, color: THEME.border, textDecorationLine: 'line-through' },
  buyBtn: { borderRadius: 12, paddingVertical: 14, alignItems: 'center', marginTop: 16 },
  buyBtnDisabled: { opacity: 0.6 },
  buyBtnText: { color: '#fff', fontSize: 15, fontWeight: '800' },
  freePlanBtn: { backgroundColor: '#27AE60' },

  downgradeBanner: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 16, backgroundColor: THEME.bg, borderRadius: 10, padding: 12 },
  downgradeText: { flex: 1, fontSize: 13, color: THEME.textLight },

  disclaimer: { fontSize: 11, color: THEME.textLight, textAlign: 'center', marginTop: 8, lineHeight: 16 },

  paymentInfoBox: {
    marginTop: 8, borderRadius: 14, overflow: 'hidden',
    borderWidth: 1, borderColor: '#BBDEFB',
    backgroundColor: '#E3F2FD',
  },
  paymentInfoRow: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 10,
    paddingHorizontal: 14, paddingVertical: 11,
  },
  paymentInfoDivider: { height: 1, backgroundColor: '#BBDEFB', marginHorizontal: 14 },
  paymentInfoText: { flex: 1, fontSize: 12, color: '#1565C0', lineHeight: 18 },
  refundPolicyLink: { textDecorationLine: 'underline', fontWeight: '600' },
  profileBlockBanner: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 10,
    backgroundColor: '#FEF3C7', borderRadius: 12, padding: 14, marginBottom: 4,
    borderWidth: 1, borderColor: '#FCD34D',
  },
  profileBlockText: { flex: 1, fontSize: 13, color: '#92400E', lineHeight: 20 },
  freeProfileNote: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: '#D1FAE5', borderRadius: 10, padding: 12, marginTop: 16,
  },
  freeProfileNoteText: { flex: 1, fontSize: 13, fontWeight: '700', color: '#065F46' },
});
